import CircularProgress from "@material-ui/core/CircularProgress";
import Icon from "@material-ui/core/Icon";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import classnames from "classnames";
import { withFormik } from "formik";
import capitalize from "lodash/capitalize";
import each from "lodash/each";
import map from "lodash/map";
import merge from "lodash/merge";
import pick from "lodash/pick";
import React, { Component } from "react";
import { Row, Col } from "react-grid-system";
import { connect } from "react-redux";

import { fetchTeammates } from "../../../../actions/recruiters/teammateActions";
import { updateProfile } from "../../../../actions/shared/individualActions";
import { openModal } from "../../../../actions/shared/uiActions";
import { getCurrentIndividual } from "../../../../selectors/individualSelectors";
import {
  disabledProps,
  alpha2CountryOptions,
  stateOptions,
  recruitmentServiceOfferingOptions,
} from "../../../../util/formHelpers";
import {
  object, string, ref, array, number
} from "../../../../util/yup";
import AvatarEditorButton from "../../../forms/custom/AvatarEditor/AvatarEditorButton";
import Button from "../../../forms/custom/Button";
import DropdownInput from "../../../forms/custom/DropdownInput.react";
import MultiLineTextField from "../../../forms/custom/MultiLineTextField";
import PhoneInput from "../../../forms/custom/PhoneInput";
import ProvideCharacterCounter from "../../../forms/custom/ProvideCharacterCounter";
import GlobalAvatar from "../../../General/GlobalAvatar";
import PageCard from "../../../General/PageCard";
import PageCardRowHeader from "../../../General/PageCardRowHeader";
import withSnackbar from "../../../General/withSnackbar";
import ToggleDropdownSelector from "../../../recruiter/Activation/Cards/MatchPreferences/ToggleDropdownSelector";

import DeactivateModal from "./modals/DeactivateModal";

const styles = {
  dropdownSelectorContainer: {
    marginLeft: -39,
  },
  avatarLabel: {
    textAlign: "center",
    marginBottom: 6,
  },
  headerAction: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    position: "relative",
  },
  headerActionError: {
    position: "absolute",
    width: "320px",
    right: 198,
  },
  deactivateContainer: {
    maxWidth: "100%",
    alignItems: "right",
  },
  numberInput: {
    "&[type=number]": {
      "-moz-appearance": "textfield",
    },
    "&::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "&::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
};

export function WorkEmailTooltip() {
  return (
    <div style={{ paddingBottom: 2 }}>
      Work Email
      <Tooltip
        id="tooltip-icon"
        title={<span style={{ fontSize: "12px" }}>This is the email you will log in with.</span>}
      >
        <Icon style={{
          color: "#546e7a", fontSize: 18, marginLeft: 3, marginBottom: -4
        }}
        >
          info_outline
        </Icon>
      </Tooltip>
    </div>
  );
}

class IndividualProfile extends Component {
  constructor(props) {
    super(props);
    this.state = { editing: false };
  }

  componentDidMount() {
    if (this.props.individual.recruiter) {
      this.props.fetchRecruiterTeammates();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      values, setFieldValue, handleReset, errors, isSubmitting
    } =
      this.props;
    if (prevState.editing && !this.state.editing) {
      handleReset();
    } else if (
      prevProps.isSubmitting &&
      !isSubmitting &&
      !Object.values(errors).length
    ) {
      this.setState({ editing: false });
    } else if (!prevProps.values.new_password && values.new_password) {
      setFieldValue("password_update", true);
    } else if (prevProps.values.new_password && !values.new_password) {
      setFieldValue("password_update", false);
    }
  }

  getErrorText(key) {
    const { errors, touched } = this.props;

    return (this.state.editing && touched[key] && errors[key]) || "";
  }

  createTextField(name, fieldProps = {}, overrideInputProps = {}, customLabel = false) {
    const { values, handleChange, handleBlur } = this.props;
    const formKey = name.replace(/ /g, "_");
    const errorText = this.getErrorText(formKey);

    if (!this.state.editing) {
      fieldProps = { ...disabledProps(true), ...fieldProps };
    }

    return (
      <Col sm={12} md={6} lg={4} style={{ padding: "0 20px 20px 0" }}>
        <TextField
          error={Boolean(errorText)}
          helperText={errorText}
          id={formKey}
          label={customLabel || name}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          value={values[formKey]}
          inputProps={merge(
            {},
            {
              "data-cy": `individual-profile-textfield-${formKey}`,
            },
            overrideInputProps
          )}
          {...fieldProps}
        />
      </Col>
    );
  }

  createDropdown(name, options, fieldProps = {}) {
    const { values, handleChange, handleBlur } = this.props;
    const formKey = name.replace(/ /g, "_");
    const errorText = this.getErrorText(formKey);

    if (!this.state.editing) {
      fieldProps = { ...disabledProps(true), ...fieldProps };
    }

    return (
      <Col sm={12} md={6} lg={4} style={{ padding: "0 20px 20px 0" }}>
        <DropdownInput
          disabled={!this.state.editing}
          id={formKey}
          error={Boolean(errorText)}
          helperText={errorText}
          label={name === "country code" ? "Country" : name}
          options={options}
          noShrink
          onChange={handleChange}
          handleBlur={handleBlur}
          value={values[formKey]}
          selected={null}
          inputProps={{
            "data-cy": `individual-profile-dropdownfield-${formKey}`,
          }}
          {...fieldProps}
        />
      </Col>
    );
  }

  createMultiLineTextField(name, customLabel = name, fieldProps = {}) {
    const { values, handleChange, handleBlur } = this.props;
    const formKey = name.replace(/ /g, "_");
    const errorText = this.getErrorText(formKey);

    if (!this.state.editing) {
      fieldProps = { ...disabledProps(true), ...fieldProps };
    }

    return (
      <Col sm={8} style={{ padding: "0 20px 20px 0" }}>
        <ProvideCharacterCounter
          compareCount={5000}
          visible={this.state.editing}
        >
          <MultiLineTextField
            label={customLabel}
            id={formKey}
            error={Boolean(errorText)}
            helperText={errorText}
            multiline
            onChange={handleChange}
            value={values[formKey]}
            rows={4}
            onBlur={handleBlur}
            disabled={!this.state.editing}
            InputProps={{
              style: !this.state.editing
                ? {
                  backgroundColor: "#FFFFFF",
                  border: "none",
                }
                : {},
            }}
          />
        </ProvideCharacterCounter>
      </Col>
    );
  }

  createMultiSelector(name, options, fieldProps = {}) {
    const {
      values, setFieldValue, handleBlur, touched, errors, classes
    } =
      this.props;
    const formKey = name.replace(/ /g, "_");
    const errorText = this.getErrorText(formKey);

    if (!this.state.editing) {
      fieldProps = { ...disabledProps(true), ...fieldProps };
    }
    return (
      <Col sm={10}>
        <div className={classes.dropdownSelectorContainer}>
          <ToggleDropdownSelector
            id={formKey}
            label={name}
            errors={touched[formKey] && errors[formKey] && errorText}
            value={values[formKey]}
            editing={this.state.editing}
            setFieldValue={setFieldValue}
            options={options}
            handleBlur={handleBlur}
          />
        </div>
      </Col>
    );
  }

  handleCancelClick = () => {
    this.setState({ editing: false });
  };

  handleEditClick = () => {
    this.setState({ editing: true });
  };

  handleAvatarChange = (newFile) => {
    newFile.blob.preview = URL.createObjectURL(newFile.blob);
    newFile.blob.name = newFile.name;

    this.props.setFieldValue("avatarSubmission", newFile);
    this.props.setFieldValue("avatar", newFile.blob);
  };

  renderAvatar() {
    const { values, isSubmitting, classes } = this.props;
    const { editing } = this.state;

    return (
      <Col
        xs={12}
        md={3}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingBottom: 20,
        }}
      >
        <>
          <div
            className={classnames("static-input-label", classes.avatarLabel)}
          >
            Photo
          </div>
          <GlobalAvatar
            size="large"
            individual={this.props.individual}
            avatarIcon={
              (this.state.editing && values.avatar && values.avatar.preview) ||
              undefined
            }
            avatarAlt={
              (this.state.editing && values.avatar && values.avatar.name) ||
              undefined
            }
          />
          {editing && (
            <AvatarEditorButton
              style={{ paddingTop: 8 }}
              disabled={isSubmitting}
              width={160}
              height={160}
              borderRadius={360}
              handleSave={this.handleAvatarChange}
              modalTitleText="Photo Upload"
              buttonText="Upload Photo"
              controlHelperText="Use the sliders above to reposition your image"
            />
          )}
        </>
      </Col>
    );
  }

  renderHeaderAction = () => {
    const {
      handleSubmit, isSubmitting, submitCount, errors, classes
    } =
      this.props;
    if (this.state.editing) {
      return (
        <div className={classes.headerAction}>
          {submitCount && Object.values(errors).length ? (
            <div className={classnames("error", classes.headerActionError)}>
              Please review the fields highlighted in red.
            </div>
          ) : null}
          <Button
            color="grey"
            variant="secondary"
            onClick={this.handleCancelClick}
            disabled={isSubmitting}
            style={{ marginLeft: 24 }}
          >
            Cancel
          </Button>
          {isSubmitting && (
            <CircularProgress
              size={24}
              style={{
                position: "absolute",
                top: "20%",
                left: "33%",
                marginRight: "-12px",
                marginLeft: "-12px",
                color: "#90A4AE",
              }}
            />
          )}
          <Button
            data-cy="individual-profile-save-button"
            color="blue"
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ marginLeft: 24 }}
          >
            Save
          </Button>
        </div>
      );
    }
    return (
      <Button
        data-cy="individual-profile-edit-button"
        onClick={this.handleEditClick}
        icon="edit"
        color="blue"
        variant="secondary"
      >
        Edit
      </Button>
    );
  };

  renderPhoneInput() {
    const { values, setFieldValue } = this.props;
    const errorText =
      this.getErrorText("phone_prefix") || this.getErrorText("phone") || "";
    return (
      <Col sm={12} md={6} lg={4} style={{ padding: "0 20px 20px 0" }}>
        <PhoneInput
          disabled={!this.state.editing}
          error={Boolean(errorText)}
          helperText={errorText}
          label="Phone Number"
          onCountryChange={(_, newVal) => setFieldValue("phone_prefix", newVal)}
          onPhoneChange={(_, newVal) => setFieldValue("phone", newVal)}
          phone_prefix={values.phone_prefix}
          phone={values.phone}
        />
      </Col>
    );
  }

  renderPasswordField() {
    const { values, handleChange, handleBlur } = this.props;
    const passwordErrorText = this.getErrorText("new_password");
    const passwordConfErrorText = this.getErrorText("password_confirmation");

    if (this.state.editing) {
      return (
        <Col sm={12} md={6} lg={4} style={{ padding: "3px 20px 20px 0" }}>
          <TextField
            error={Boolean(passwordErrorText)}
            helperText={passwordErrorText}
            id="new_password"
            label="New Password"
            onChange={handleChange}
            onBlur={handleBlur}
            type="password"
            value={values.new_password || ""}
          />
          <TextField
            error={Boolean(passwordConfErrorText)}
            helperText={passwordConfErrorText}
            id="password_confirmation"
            label="Password Confirmation"
            onChange={handleChange}
            onBlur={handleBlur}
            type="password"
            value={values.password_confirmation || ""}
          />
        </Col>
      );
    }
    return (
      <Col
        sm={12}
        md={6}
        lg={4}
        style={{
          height: "161px",
          padding: "0px 20px 20px 0",
          marginTop: "-2px",
        }}
      >
        <TextField
          {...disabledProps(true)}
          id="static-password"
          label="Password"
          value="[hidden]"
        />
      </Col>
    );
  }

  renderDeactivate() {
    const { openDeactivateModal, classes } = this.props;

    return (
      <div className={classes.deactivateContainer}>
        <div
          className="pseudo-link link"
          onClick={() => openDeactivateModal()}
          style={{ textAlign: "right", marginRight: 50 }}
        >
          Deactivate My Account
        </div>
      </div>
    );
  }

  renderEmployerFields() {
    const { individual } = this.props;

    return (
      <Row>
        {this.renderAvatar()}
        <Col xs={12} md={9} style={{ display: "flex", flexWrap: "wrap" }}>
          {this.createTextField("first name")}
          {this.createTextField("last name")}
          <Col sm={12} md={6} lg={4} style={{ padding: "0 20px 20px 0" }} />
          {this.createTextField("email", { disabled: true, label: <WorkEmailTooltip /> })}
          {this.renderPhoneInput()}
          {this.createTextField("phone ext", {
            inputProps: { maxLength: 5 },
            label: "Extension",
            style: { maxWidth: "50%" },
          })}
          {!individual.sso && this.renderPasswordField()}
        </Col>
      </Row>
    );
  }

  renderRecruiterFields() {
    const { classes } = this.props;
    const { editing } = this.state;

    return (
      <>
        <PageCardRowHeader lineStyle={{ width: "calc(100% - 24px)", marginLeft: 12 }} titleStyle={{ paddingTop: 0, paddingLeft: 12 }} title="Personal Information" />
        <Row>
          {this.renderAvatar()}
          <Col xs={12} md={9} style={{ display: "flex", flexWrap: "wrap" }}>
            {this.createTextField("first name")}
            {this.createTextField("last name")}
            {this.createTextField("email", { disabled: true, label: <WorkEmailTooltip /> })}
            {this.renderPhoneInput()}
            {this.createTextField("phone ext", {
              inputProps: { maxLength: 5 },
              label: "Extension",
              style: { maxWidth: "50%" },
            })}
            {this.createTextField("personal email")}
            {this.createTextField("linkedin url")}
            {this.createDropdown("country code", alpha2CountryOptions)}
            {this.props.values.country_code === "US" &&
              this.createDropdown("state", stateOptions)}
          </Col>
        </Row>
        <PageCardRowHeader lineStyle={{ width: "calc(100% - 24px)", marginLeft: 12 }} titleStyle={{ paddingTop: 0, paddingLeft: 12 }} title="Recruiting Summary" />
        <Row>
          <Col xs={12} md={9} style={{ paddingLeft: 36, display: "flex", flexWrap: "wrap" }}>
            {this.createTextField("years experience", null, {
              type: "number",
              className: classes.numberInput,
            })}
            <Col sm={12} md={6} lg={4} style={{ padding: "0 20px 0 0" }} />
            {this.createMultiLineTextField(
              "self description",
              "Describe yourself as a recruiter"
            )}
            {this.createMultiSelector(
              "recruitment service offerings",
              recruitmentServiceOfferingOptions
            )}
          </Col>
        </Row>
        {editing && this.renderDeactivate()}
      </>
    );
  }

  render() {
    const { values, classes, individual } = this.props;
    const { editing } = this.state;
    const { isRecruiter } = values;

    return (
      <PageCard
        title={<h3 className="normal">Profile</h3>}
        action={this.renderHeaderAction}
        overflow="visible"
      >
        {isRecruiter ? this.renderRecruiterFields() : this.renderEmployerFields()}
      </PageCard>
    );
  }
}

const ProfileSettingsForm = withFormik({
  enableReinitialize: true,
  handleSubmit: (values, { props, setSubmitting, setErrors }) => {
    const submissionData = pick(values, [
      "first_name",
      "last_name",
      "personal_email",
      "phone_prefix",
      "phone",
      "phone_ext",
      "linkedin_url",
      "country_code",
      "state",
      "self_description",
      "recruitment_service_offerings",
      "years_experience",
    ]);

    submissionData.email = values.email;
    if (values.password_update) {
      submissionData.password = values.new_password;
    }

    if (values.avatarSubmission) {
      submissionData.avatar_file = values.avatarSubmission.file;
      submissionData.avatar_file_name = values.avatarSubmission.name;
      submissionData.avatar_file_type = values.avatarSubmission.type;
    }

    submissionData.recruitment_service_offerings = map(
      submissionData.recruitment_service_offerings,
      (offering) => offering.value
    );

    props
      .updateProfile(submissionData)
      .then(() => {
        props.snackbar.showMessage(
          "Your information has been successfully saved."
        );
      })
      .catch((errorResponse) => {
        const errorMessages = errorResponse.errors;
        if (errorMessages) {
          const backendErrors = {};
          let message;
          each(errorMessages, (messagesArr, key) => {
            message = messagesArr[0] || "";
            if (key === "phone" && /\d$/.test(message)) {
              message += " digits";
            }
            backendErrors[key] = capitalize(message);
          });
          setErrors(backendErrors);
        }
      })
      .then(() => setSubmitting(false));
  },
  mapPropsToValues: ({ individual }) => {
    const recruitmentServiceOfferingsValues = map(
      individual.recruitmentServiceOfferings,
      (offering) => ({
        label: offering,
        value: offering,
      })
    );

    return {
      avatar: null,
      first_name: individual.firstName,
      last_name: individual.lastName,
      email: individual.email,
      personal_email: individual.personalEmail || "",
      phone_prefix: individual.phonePrefix,
      phone: individual.phone,
      phone_ext: individual.phoneExt || "",
      password_update: false,
      new_password: "",
      password_confirmation: "",
      avatarSubmission: {},
      linkedin_url: individual.linkedinUrl,
      isRecruiter: individual.recruiter,
      country_code: individual.countryCode,
      state: individual.state,
      self_description: individual.selfDescription || "",
      recruitment_service_offerings: recruitmentServiceOfferingsValues || [],
      years_experience: individual.yearsExperience || null,
    };
  },
  validationSchema: object().shape({
    first_name: string().required("Cannot be blank"),
    last_name: string().required("Cannot be blank"),
    email: string()
      .required("Cannot be blank")
      .email("Must be a valid email address"),
    phone_prefix: string().required("Cannot be blank"),
    phone: string()
      .required("Cannot be blank")
      .matches(/^\d+$/, "Must only contain digits")
      .max(15, "Must be at most 15 characters")
      .when("phone_prefix", {
        is: "us",
        then: string()
          .required("Cannot be blank")
          .length(10, "US phone numbers must contain exactly 10 characters"),
      }),
    phone_ext: string().max(5, "Must be at most 5 characters"),
    new_password: string().when("password_update", {
      is: true,
      then: string()
        .min(8, "Must be at least 8 characters")
        .max(128, "Must be at most 128 characters"),
    }),
    password_confirmation: string().when("password_update", {
      is: true,
      then: string()
        .oneOf([ref("new_password")], "Must match password")
        .required("Must match new password"),
    }),
    linkedin_url: string()
      .nullable()
      .when("isRecruiter", {
        is: true,
        then: string()
          .nullable()
          .required("Cannot be blank")
          .validLinkedinUrl()
          .validURL(true),
      }),
    country_code: string()
      .nullable()
      .when("isRecruiter", {
        is: true,
        then: string().nullable().required("Cannot be blank"),
      }),
    state: string()
      .nullable()
      .when("isRecruiter", {
        is: true,
        then: string().when("country_code", {
          is: "US",
          then: string().nullable().required("Cannot be blank"),
          otherwise: string().nullable(),
        }),
      }),
    self_description: string()
      .nullable()
      .when("isRecruiter", {
        is: true,
        then: string()
          .nullable()
          .required("Cannot be blank")
          .max(5000, "Must be at most 5000 characters"),
      }),
    years_experience: number()
      .nullable()
      .when("isRecruiter", {
        is: true,
        then: number().nullable().required("Cannot be blank"),
      }),
    recruitment_service_offerings: array()
      .nullable()
      .when("isRecruiter", {
        is: true,
        then: array().min(1, "Cannot be blank"),
      }),
  }),
})(IndividualProfile);

const mapStateToProps = (state) => {
  const currentIndividual = getCurrentIndividual(state);

  return {
    individual: currentIndividual,
    roles: currentIndividual.roles,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchRecruiterTeammates: () => dispatch(fetchTeammates()),
  updateProfile: (data) => dispatch(updateProfile(data)),
  openDeactivateModal: () => dispatch(openModal(<DeactivateModal />)),
});

export default withStyles(styles)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withSnackbar()(ProfileSettingsForm))
);
